<template>

    <el-form-item
        :rules="{ required: isFieldRequired, validator: validateField, trigger: 'change' }"
        :prop="fieldProp(field)">

        <span slot="label">
            {{ field.label }}
            <span v-if="!_.isEmpty(field.content)" class="fin-relative fin-z-50 fin-ml-1"
                :class="isFrontend ? 'fin-hidden' : 'fin-hidden 2xl:fin-inline-flex'">
                <el-popover
                    placement="top-start"
                    :title="field.label"
                    width="200"
                    trigger="hover"
                    :content="field.content"
                    :disabled="_.isEmpty(field.content)"
                    popper-class="app-form-field-popover"
                    >
                    <i slot="reference" class="fas fa-circle-info fin-ml-2 fin-text-warning"></i>
                </el-popover>
            </span>
        </span>

        <div v-if="!_.isEmpty(field.content)" class="content fin-flex fin-w-full"
            :class="isFrontend ? '' : '2xl:fin-hidden'">
            {{ field.content }}
        </div>

        <place-auto-complete
            v-model="inputValue"
            :api-key="global('tenant.google_maps_api_key')"
            :bounds="{ north: -10.6681857235, south: -43.6345972634, east: 153.569469029, west: 113.338953078 }"
            :placeholder="field.placeholder"
            :autocomplete="true"
            v-place-autofill:formatted_address="addressComponents.formatted_address"
            v-place-autofill:subpremise="addressComponents.unit_number"
            v-place-autofill:street_number="addressComponents.street_number"
            v-place-autofill:route.short="addressComponents.route"
            v-place-autofill:city="addressComponents.locality"
            v-place-autofill:state.short="addressComponents.administrative_area_level_1"
            v-place-autofill:zipcode="addressComponents.postal_code"
            v-place-autofill:country.short="addressComponents.country"
            :disabled="addressDisabled()"
            :showActivityIndicator="true"
            @autocomplete-select="outputData"
            @focus="setCurrentField(field)"
        ></place-auto-complete>

    </el-form-item>
</template>

<script>
import formMixin from "@tenant/app-form/mixins/formMixin"
import PlaceAutoComplete from "@shared/components/form/PlaceAutoComplete.vue"

export default {
    name: "AddressField",

    components: {
        PlaceAutoComplete,
    },

    mixins: [formMixin],

    props: {
        field: {
            required: true,
            type: [Object, Array],
        },
        fieldKey: {
            required: true,
            type: Number,
        },
        path: {
            required: true,
            type: [String, Number],
        },
        value: {
            required: false,
        },
        formStore: {
            required: false,
            type: String,
            default: "formStore"
        },
    },

    data() {
        return {
            addressComponents: {
                id: null,
                formatted_address: null,
                unit_number: null,
                street_number: null,
                route: null,
                locality: null,
                administrative_area_level_1: null,
                administrative_area_level_2: null,
                postal_code: null,
                country: null,
            },
            inputValue: null,
            stopWatch: false,
        }
    },

    computed: {
        isFieldRequired() {
            return this.field.isRequired
        },
    },

    mounted() {
        
        let path = this.path

        if (this.path.includes('.formatted_address')) {
            this.$store.dispatch(`${this.formStore}/form/setPagePaths`, this.path)
            path = path.replace('.formatted_address', '')
        }

        this.$store.dispatch(`${this.formStore}/form/setPagePaths`, `${path}.id`)
        this.$store.dispatch(`${this.formStore}/form/setPagePaths`, `${path}.unit_number`)
        this.$store.dispatch(`${this.formStore}/form/setPagePaths`, `${path}.street_number`)
        this.$store.dispatch(`${this.formStore}/form/setPagePaths`, `${path}.route`)
        this.$store.dispatch(`${this.formStore}/form/setPagePaths`, `${path}.locality`)
        this.$store.dispatch(`${this.formStore}/form/setPagePaths`, `${path}.administrative_area_level_1`)
        this.$store.dispatch(`${this.formStore}/form/setPagePaths`, `${path}.administrative_area_level_2`)
        this.$store.dispatch(`${this.formStore}/form/setPagePaths`, `${path}.postal_code`)
        this.$store.dispatch(`${this.formStore}/form/setPagePaths`, `${path}.country`)
        this.$store.dispatch(`${this.formStore}/form/setPagePaths`, `${path}.formatted_address`)

        if (_.isString(this.value) && !_.isEmpty(this.value)) {

            let pathArray = this.path.split('.')

            if (this.field.field_mapping == 'formatted_address') {
                // remove the formatted address key from the path
                pathArray.splice(_.size(pathArray) - 1)
                let path = pathArray.join('.')
                let addressData = _.get(this.formData, path)
                this.addressComponents = addressData
            } else if (this.field.field_mapping == 'address') {
                let addressData = _.get(this.formData, this.path)
                this.addressComponents = addressData
            }
        }

        this.outputData()
    },

    methods: {
        validateField(rule, value, callback) {
            if (!this.inputValue && this.isFieldRequired) {
                callback(new Error(`${this.field.label} is Required`))
            } else if (this.field.minChars && _.size(this.inputValue) < this.field.minChars) {
                callback(new Error(`${this.field.label} is Required`))
            } else {
                callback()
            }
        },
        addressDisabled() {
            return false
        },
        outputData() {
            
            this.$store.dispatch(`${this.formStore}/form/setNavigationActive`, false)

            setTimeout(() => {

                // only pass back what we are updating
                let updateData = {
                    id: _.get(this.addressComponents, 'id', null),
                    full_address: _.get(this.addressComponents, 'full_address', null),
                    formatted_address: _.get(this.addressComponents, 'formatted_address', null),
                    unit_number: _.get(this.addressComponents, 'unit_number', null),
                    street_number: _.get(this.addressComponents, 'street_number', null),
                    route: _.get(this.addressComponents, 'route', null),
                    locality: _.get(this.addressComponents, 'locality', null),
                    administrative_area_level_1: _.get(this.addressComponents, 'administrative_area_level_1', null),
                    administrative_area_level_2: _.get(this.addressComponents, 'administrative_area_level_2', null),
                    postal_code: _.get(this.addressComponents, 'postal_code', null),
                    country: _.get(this.addressComponents, 'country', null),
                }

                // filter out any null values
                for (const key in updateData) {
                    if (updateData[key] === null || typeof updateData[key] === "undefined") {
                        delete updateData[key]
                    }
                }

                if (this.field.field_mapping == 'formatted_address') {
                    let path = String(this.path).split(".")
                    path.splice(_.size(path) - 1)
                    this.setPathData(path, updateData)
                } else if (this.field.field_mapping == 'address') {
                    let path = String(this.path).split(".")
                    this.setPathData(path, updateData)
                }

                this.checkRules(this.field, this.path)

                this.$store.dispatch(`${this.formStore}/form/setNavigationActive`, true)

            }, 1000)
        },
    },

    watch: {
        value: {
            handler(value, oldValue) {
                if (_.isObject(value)) {
                    this.updateWatch = false
                    this.stopWatch = true
                    this.addressComponents = value
                    this.inputValue = value.formatted_address
                } else {
                    this.inputValue = value
                }
            },
            deep: true,
            immediate: true,
        },
    },
}
</script>

<style lang="scss" scoped></style>
